import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import {
  BsShieldCheck,
  BsClipboardCheck,
  BsExclamationTriangle,
  BsFileEarmarkText,
  BsMortarboard,
  BsWrench,
  BsChevronDown,
  BsPlus
} from 'react-icons/bs';

import DatePicker from '../../../components/DatePicker';

import {
  useDashboard
} from '../context/DashboardContext';



export default function RestaurantDashboardHeader() {

  // =====================================================
  // DASHBOARD CONTEXT
  // =====================================================

  const {
    selectedDate,
    setSelectedDate,
    dashboardData, loading, errors,
  } = useDashboard();

  // =====================================================
  // REDUX
  // =====================================================

  const navigate = useNavigate();
  const user = useSelector(state => state.auth?.user?.details);
  const metrics = dashboardData.header;
  const value = key => loading.header ? '…' : errors.header ? '—' : metrics?.[key] ?? '—';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  const {
    data: checklistStats,
    loading: checklistStatsLoading,
    error: checklistStatsError
  } = useSelector(
    (state) => state.checks.checklistStats
  );

  // =====================================================
  // FETCH TODAY'S CHECK STATS
  // =====================================================



  // =====================================================
  // TODAY'S CHECK VALUES
  // =====================================================

  const totalTasks = checklistStats?.totalTasks ?? 0;

  const completedTasks =
    checklistStats?.completedTasks ?? 0;

  const completionPercentage =
    Number(checklistStats?.completionPercentage ?? 0);

  return (
    <div className="w-full font-sans text-[#1e293b]">

      {/* HEADER SECTION */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-6">

        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-slate-800">
            {greeting}, {(user?.fullName || metrics?.fullName)?.split(' ')[0] || 'there'} <span className="animate-bounce">👋</span>
          </h1>

          <p className="text-slate-400 text-sm mt-0.5">
            Here's your restaurant operations overview.
          </p>
        </div>

        {/* ACTION CONTROLS */}
        <div className="flex flex-wrap items-center gap-3">

          {/* CONTEXT DATE PICKER */}
          <DatePicker
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />

          {/* Location Dropdown */}
          <button
            type="button"
            disabled
            title="Outlet filtering is not available in the current schema"
            className="flex items-center gap-2 bg-white border border-slate-200 px-4 py-2 rounded-xl text-sm font-medium text-slate-600 shadow-sm hover:bg-slate-50 transition"
          >
            <span>All Outlets</span>

            <BsChevronDown className="text-slate-400 text-xs ml-4" />
          </button>

          {/* Primary Action Button */}
          <button
            type="button"
            onClick={() => navigate(`/checks?date=${selectedDate}`)}
            className="flex items-center gap-1 bg-[#3a31d8] hover:bg-[#2c24b5] text-white px-4 py-2 rounded-xl text-sm font-semibold shadow-sm transition"
          >
            <BsPlus className="text-lg" />

            <span>Quick Action</span>

            <BsChevronDown className="text-xs opacity-80 ml-1" />
          </button>

        </div>
      </div>

      {/* KPI GRID */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">

        {/* =====================================================
            CARD 1: OVERALL COMPLIANCE
        ====================================================== */}

        <Link to="/compliance-center" aria-label="View overall compliance" className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm flex flex-col justify-between min-h-[155px]">

          <div className="flex items-start justify-between">

            <div>
              <p className="text-[11px] font-bold text-slate-400 tracking-wider uppercase">
                Overall Compliance
              </p>

              <div className="flex items-baseline gap-2 mt-2">

                <span className="text-3xl font-bold tracking-tight text-slate-800">
                  {value('compliance')}{metrics?.compliance != null ? '%' : ''}
                </span>

                <span className="text-[11px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-md">
                  {errors.header ? 'Unable to load' : loading.header ? 'Loading…' : metrics?.complianceStatus || 'No records'}
                </span>

              </div>
            </div>

            <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl">
              <BsShieldCheck className="text-xl" />
            </div>

          </div>

          <div className="flex items-center justify-between border-t border-slate-50 pt-3 mt-4 text-[11px] font-semibold text-slate-400">
            <span title="Percentage of approved recorded check results that passed">Target: 90%</span>

            <span className="text-green-600">
              {metrics?.change == null ? 'No comparison data' : `${metrics.change >= 0 ? '↑' : '↓'} ${Math.abs(metrics.change)}% vs previous day`}
            </span>
          </div>

        </Link>

        {/* =====================================================
            CARD 2: TODAY'S CHECKS
        ====================================================== */}

        <Link to="/checks" aria-label="View today's checks" className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm flex flex-col justify-between min-h-[155px]">

          <div className="flex items-start justify-between">

            <div>

              <p className="text-[11px] font-bold text-slate-400 tracking-wider uppercase">
                Today's Checks
              </p>

              <div className="mt-2">

                {checklistStatsLoading ? (
                  <>
                    <div className="h-9 w-24 bg-slate-100 rounded-lg animate-pulse" />

                    <p className="text-[11px] text-slate-400 font-medium mt-1">
                      Loading...
                    </p>
                  </>
                ) : checklistStatsError ? (
                  <>
                    <span className="text-3xl font-bold tracking-tight text-slate-800">
                      —
                    </span>

                    <p className="text-[11px] text-red-400 font-medium mt-0.5">
                      Unable to load
                    </p>
                  </>
                ) : (
                  <>
                    <span className="text-3xl font-bold tracking-tight text-slate-800">

                      {completedTasks}

                      <span className="text-slate-300 font-normal text-xl">
                        {' '}/ {totalTasks}
                      </span>

                    </span>

                    <p className="text-[11px] text-slate-400 font-medium mt-0.5">
                      Completed
                    </p>
                  </>
                )}

              </div>

            </div>

            <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl">
              <BsClipboardCheck className="text-xl" />
            </div>

          </div>

          {/* PROGRESS BAR */}
          <div className="mt-4 pt-2">

            <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">

              <div
                className="bg-green-600 h-full rounded-full transition-all duration-500"
                style={{
                  width: `${Math.min(
                    Math.max(completionPercentage, 0),
                    100
                  )}%`
                }}
              />

            </div>

          </div>

        </Link>

        {/* =====================================================
            CARD 3: OPEN ISSUES
        ====================================================== */}

        <Link to="/incident-log" aria-label="View open issues in the incident log" className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm flex flex-col justify-between min-h-[155px]">

          <div className="flex items-start justify-between">

            <div>

              <p className="text-[11px] font-bold text-slate-400 tracking-wider uppercase">
                Open Issues
              </p>

              <div className="mt-2">

                <span className="text-3xl font-bold tracking-tight text-slate-800">
                  {value('openIssues')}
                </span>

                <p className="text-[11px] text-slate-400 font-medium mt-0.5">
                  Requires attention
                </p>

              </div>

            </div>

            <div className="p-2.5 bg-orange-50 text-orange-500 rounded-xl">
              <BsExclamationTriangle className="text-xl" />
            </div>

          </div>

          <div className="flex items-center gap-2 border-t border-slate-50 pt-3 mt-4 text-[11px] font-bold">

            <span className="text-red-500">
              Severity not tracked
            </span>

            <span className="text-slate-300">
              •
            </span>

            <span className="text-amber-500">
              Open incidents
            </span>

          </div>

        </Link>

        {/* =====================================================
            CARD 4: INCIDENTS
        ====================================================== */}

        <Link to="/incident" aria-label="View incidents" className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm flex flex-col justify-between min-h-[155px]">

          <div className="flex items-start justify-between">

            <div>

              <p className="text-[11px] font-bold text-slate-400 tracking-wider uppercase">
                Incidents{' '}
                <span className="text-slate-400 font-normal lowercase">
                  (This Month)
                </span>
              </p>

              <div className="mt-2">

                <span className="text-3xl font-bold tracking-tight text-slate-800">
                  {value('incidents')}
                </span>

                <p className="text-[11px] text-slate-400 font-medium mt-0.5">
                  Reported
                </p>

              </div>

            </div>

            <div className="p-2.5 bg-slate-900 text-white rounded-xl">
              <BsFileEarmarkText className="text-xl" />
            </div>

          </div>

          <div className="flex items-center gap-2 border-t border-slate-50 pt-3 mt-4 text-[11px] font-medium text-slate-500">

            <span>
              {value('incidentsOpen')} Open
            </span>

            <span className="text-slate-300">
              •
            </span>

            <span>
              {value('incidentsClosed')} Closed
            </span>

          </div>

        </Link>

        {/* =====================================================
            CARD 5: STAFF TRAINING
        ====================================================== */}

        <Link to="/staff-training" aria-label="View staff training" className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm flex flex-col justify-between min-h-[155px]">

          <div className="flex items-start justify-between">

            <div>

              <p className="text-[11px] font-bold text-slate-400 tracking-wider uppercase">
                Staff Training
              </p>

              <div className="mt-2">

                <span className="text-3xl font-bold tracking-tight text-slate-800">
                  —
                </span>

                <p className="text-[11px] text-slate-400 font-medium mt-0.5">
                  Not tracked
                </p>

              </div>

            </div>

            <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl">
              <BsMortarboard className="text-xl" />
            </div>

          </div>

          <div className="border-t border-slate-50 pt-3 mt-4 text-[11px] font-medium text-slate-500">
            <span>
              Expiry dates not tracked
            </span>
          </div>

        </Link>

        {/* =====================================================
            CARD 6: EQUIPMENT
        ====================================================== */}

        <Link to="/equipment" aria-label="View equipment" className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm flex flex-col justify-between min-h-[155px]">

          <div className="flex items-start justify-between">

            <div>

              <p className="text-[11px] font-bold text-slate-400 tracking-wider uppercase">
                Equipment
              </p>

              <div className="mt-2">

                <span className="text-3xl font-bold tracking-tight text-slate-800">
                  {value('activeEquipment')}
                </span>

                <p className="text-[11px] text-slate-400 font-medium mt-0.5">
                  Active
                </p>

              </div>

            </div>

            <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl">
              <BsWrench className="text-xl" />
            </div>

          </div>

          <div className="border-t border-slate-50 pt-3 mt-4 text-[11px] font-bold text-red-500">
            <span>
              {value('overdueEquipment')} Overdue
            </span>
          </div>

        </Link>

      </div>
    </div>
  );
}
